import React, { useState, useEffect, useMemo } from "react";
import { Typography, Box, useTheme, IconButton, Button } from "@mui/material";
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import CoffeeIcon from '@mui/icons-material/Coffee';
import UpcomingIcon from '@mui/icons-material/Upcoming';
import Header from "../../components/Header";
import Users from "../../components/Users";
import RealTimeClock from "../../components/Time";
import { cobotColours } from "../../theme/theme";
import InputBase from "@mui/material/InputBase";
import SearchIcon from "@mui/icons-material/Search";
import moment from "moment";
import api from "../../services/api";

const MainDashboard = () => {
    const cobotTheme = useTheme();
    const colors = cobotColours(cobotTheme.palette);
    const [selectedFilter, setSelectedFilter] = useState("All");
    
    const [employees, setEmployees] = useState([]);
    const [clockings, setClockings] = useState([]);
    const [breaks, setBreaks] = useState([]);
    const [loadingNumbers, setLoadingNumbers] = useState(true);

    const filterOptions = ["All", "Upcoming Shift", "On Break", "Working"];

    useEffect(() => {
        const fetchDataForNumbers = async () => {
            setLoadingNumbers(true);
            try {
                const [empData, clockingsData, breaksData] = await Promise.all([
                    api.employee.getAll(),
                    api.shiftActivities.getAllClockings(),
                    api.shiftActivities.getAllBreaks()
                ]);

                setEmployees(empData.success ? empData.data : []);
                setClockings(clockingsData.success ? clockingsData.data : []);
                setBreaks(breaksData.success ? breaksData.data : []);

            } catch (err) {
                console.error("Error fetching data for numbers:", err);
            } finally {
                setLoadingNumbers(false);
            }
        };
        fetchDataForNumbers();
    }, []);

    const todayStats = useMemo(() => {
        let workingCount = 0;
        let onBreakCount = 0;
        let upcomingCount = 0;

        employees.forEach(emp => {
            const currentClocking = clockings.find(
                c => c.employeeId === emp._id && !c.clockOutTime
            );
            let currentBreak = null;
            if (currentClocking) {
                currentBreak = breaks.find(
                    b => b.clockingId === currentClocking._id && !b.breakEndTime
                );
            }
            const now = moment();
            if (currentClocking) {
                if (currentBreak) {
                    onBreakCount++;
                } else {
                    workingCount++;
                }
            } else if (emp.shifts && emp.shifts.length > 0) {
                const isUpcoming = emp.shifts.some(s => 
                    moment(s.startTime).isAfter(now) && 
                    moment(s.startTime).isBefore(moment(now).add(8, 'hours'))
                );
                if (isUpcoming) {
                    upcomingCount++;
                }
            }
        });
        return { workingCount, onBreakCount, upcomingCount };
    }, [employees, clockings, breaks]);

    return (
        <Box
            height="100vh"
            display="flex"
            flexDirection="column"
            backgroundColor={colors.primary.special}
            padding={2}
        >
            {/* Header Section */}
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                backgroundColor={colors.primary.teal}
                paddingX={2}
                paddingY={0.5}
            >
                <Box sx={{ flexGrow: 1, textAlign: 'center' }}>
                <Header title="MAIN DASHBOARD" />
                </Box>
                <Box sx={{ width: 100, visibility: 'hidden' }} />
            </Box>

            {/* Main Content */}
            <Box
                flex={1}
                display="flex"
                padding="10px 0"
                gap={2}
            >
                {/* Left Panel */}
                <Box
                    width={{ xs: "100%", md: "25%" }}
                    display="flex"
                    flexDirection="column"
                    gap={2}
                >
                    <Box
                        width="100%"
                        height="100px"
                        borderRadius="10px"
                        backgroundColor={colors.grey.lighter}
                        display="flex" justifyContent="center"
                        alignItems="center"
                    >
                        <RealTimeClock />
                    </Box>
                    <Box
                        flex={2}
                        height="auto"
                        minHeight="100px"
                        borderRadius="10px"
                        padding={2}
                        backgroundColor={colors.grey.lighter}
                        display="flex" justifyContent="center"
                        alignItems="center"
                        textAlign="center"
                    >
                        <Typography variant="caption">
                            Additional Info / Quick Actions
                        </Typography>
                    </Box>
                    <Box
                        flex={.6}
                        borderRadius="10px"
                        backgroundColor={colors.grey.lighter}
                        display="flex"
                        flexDirection="column"
                        justifyContent="space-around"
                        padding={3}
                    >
                        <Typography variant="h6" fontWeight="bold" color={colors.primary.teal}>
                            Today's Snapshot
                        </Typography>
                        
                        {loadingNumbers ? (
                            <Typography>Loading numbers...</Typography>
                        ) : (
                            <Box width="100%">
                                <Box display="flex" justifyContent="space-between" width="100%" my={0.5}>
                                    <Box display="flex" alignItems="center">
                                        <WorkOutlineIcon sx={{ color: colors.secondary.green, fontSize: "1.2rem", mr: 1 }} />
                                        <Typography variant="h6">People on shift</Typography>
                                    </Box>
                                    <Typography variant="h6" fontWeight="bold">{todayStats.workingCount}</Typography>
                                </Box>
                                <Box display="flex" justifyContent="space-between" width="100%" my={0.5}>
                                    <Box display="flex" alignItems="center">
                                        <CoffeeIcon sx={{ color: colors.secondary.yellow, fontSize: "1.2rem", mr: 1 }} />
                                        <Typography variant="h6">People on break</Typography>
                                    </Box>
                                    <Typography variant="h6" fontWeight="bold">{todayStats.onBreakCount}</Typography>
                                </Box>
                                <Box display="flex" justifyContent="space-between" width="100%" my={0.5}>
                                    <Box display="flex" alignItems="center">
                                        <UpcomingIcon sx={{ color: colors.secondary.blue, fontSize: "1.2rem", mr: 1 }} />
                                        <Typography variant="h6">Upcoming shifts</Typography>
                                    </Box>
                                    <Typography variant="h6" fontWeight="bold">{todayStats.upcomingCount}</Typography>
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Box>

                {/* Right Panel */}
                <Box
                    flex={1}
                    display="flex"
                    flexDirection="column"
                    gap={1} 
                >
                    <Box
                        display="block" 
                        width="100%"
                        alignItems="center" 
                        mb={1}
                    >
                        <Box 
                        borderRadius="10px"
                        display="flex"
                        backgroundColor={colors.grey.lighter}
                        alignItems="center"
                        px={2}
                            mb={2}
                            sx={{ flexGrow: { xs: 1, md: 0.5 } }}
                    >
                            <IconButton type="button" sx={{ p: 1 }}>
                            <SearchIcon />
                        </IconButton>
                        <InputBase sx={{ ml: 2, flex: 1 }} placeholder="Search by name" />
                        </Box>
                        
                        <Box display="flex" gap="10px" >
                            {filterOptions.map((option) => (
                                <Button
                                    key={option}
                                    variant={selectedFilter === option ? "contained" : "outlined"}
                                    onClick={() => setSelectedFilter(option)}
                                    sx={{
                                        backgroundColor: selectedFilter === option ? colors.primary.teal : colors.grey.lighter,
                                        color: selectedFilter === option ? colors.text.white : colors.text.black,
                                        borderColor: colors.primary.teal,
                                        fontWeight: "bold",
                                        fontSize: "1rem",
                                        padding: "10px 20px",
                                        borderRadius: "5%",
                                        margin: "0 0px"
                                    }}
                                >
                                    {option}
                                </Button>
                            ))}
                        </Box>


                    </Box>
                    
                    <Box flex={1} overflow="auto" >
                        <Users selectedFilter={selectedFilter} />
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default MainDashboard;
